import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import api from '../api/client';
import { toast } from 'sonner';
import ImageUpload, { resolveImage } from '../components/admin/ImageUpload';
import EditableText from '../components/admin/EditableText';
import CategoryTree from '../components/admin/CategoryTree';
import DatasheetUpload from '../components/admin/DatasheetUpload';
import CustomerOverridesDialog from '../components/admin/CustomerOverridesDialog';
import StaticPagesAdmin from '../components/admin/StaticPagesAdmin';
import CategoryProductsAccordion from '../components/admin/CategoryProductsAccordion';
import EmailTemplatesAdmin from '../components/admin/EmailTemplatesAdmin';
import AbandonedCartsAdmin from '../components/admin/AbandonedCartsAdmin';
import ReviewsAdmin from '../components/admin/ReviewsAdmin';
import FAQsAdmin from '../components/admin/FAQsAdmin';
import SettingsAdmin from '../components/admin/SettingsAdmin';
import HomepageCurationAdmin from '../components/admin/HomepageCurationAdmin';
import OrdersAdmin from '../components/admin/OrdersAdmin';
import CustomersAdmin from '../components/admin/CustomersAdmin';
import HeroBannersAdmin from '../components/admin/HeroBannersAdmin';
import ManageAdminsAdmin from '../components/admin/ManageAdminsAdmin';
import ActivityLogAdmin from '../components/admin/ActivityLogAdmin';
import SearchAnalyticsAdmin from '../components/admin/SearchAnalyticsAdmin';
import PromoCodesAdmin from '../components/admin/PromoCodesAdmin';
import { Loader2, Package, Grid, Image as ImageIcon, ShieldCheck, ArrowLeft, Plus, ClipboardList, Users, Settings as SettingsIcon, ChevronDown, ChevronRight, Download, Upload, Wallet, FileText, Mail, ShoppingCart, MessageSquare, HelpCircle, UserCog, Activity, Search as SearchIcon, LayoutGrid, Tag } from 'lucide-react';

// Each tab declares which permission (or super-admin) is required.
// `perm: null` = any admin. `super: true` = super admin only.
const TABS = [
  { id: 'products',   label: 'Products',        icon: Package,       perm: 'products' },
  { id: 'categories', label: 'Categories',      icon: Grid,          perm: 'products' },
  { id: 'hero',       label: 'Hero Banners',    icon: ImageIcon,     perm: 'seo' },
  { id: 'orders',     label: 'Orders',          icon: ClipboardList, perm: 'orders' },
  { id: 'customers',  label: 'Customers',       icon: Users,         perm: 'customer_privacy' },
  { id: 'reviews',    label: 'Reviews',         icon: MessageSquare, perm: 'reviews' },
  { id: 'faqs',       label: 'FAQs',            icon: HelpCircle,    perm: 'seo' },
  { id: 'emails',     label: 'Emails',          icon: Mail,          perm: 'emails' },
  { id: 'promos',     label: 'Promo Codes',     icon: Tag,           perm: 'orders' },
  { id: 'abandoned',  label: 'Abandoned Carts', icon: ShoppingCart,  perm: 'orders' },
  { id: 'search-analytics', label: 'Search Analytics', icon: SearchIcon, perm: 'sales_reports' },
  { id: 'homepage',   label: 'Homepage',        icon: LayoutGrid,    perm: 'seo' },
  { id: 'settings',   label: 'Site settings',   icon: SettingsIcon,  super: true },
  { id: 'team',       label: 'Manage Admins',   icon: UserCog,       super: true },
  { id: 'activity',   label: 'Activity Log',    icon: Activity,      super: true },
];

const ICON_OPTIONS = ['ShoppingBag', 'Truck', 'RotateCcw', 'Wrench', 'Sparkles', 'Package', 'Tag', 'Heart', 'Home', 'Hammer', 'Star'];

const AdminPage = () => {
  const { user, loading } = useAuth();
  const { settings: siteSettings, refresh: refreshSettings } = useSettings();
  // Full settings doc — includes secret-typed fields (API keys) hidden from
  // the public /api/settings endpoint. Fetched only for admins.
  const [adminSettings, setAdminSettings] = useState(null);
  const refreshAdminSettings = useCallback(async () => {
    try {
      const r = await api.get('/admin/settings');
      setAdminSettings(r.data);
    } catch (e) { /* non-admin or transient — fall back to public subset */ }
  }, []);
  useEffect(() => {
    if (user?.is_admin) refreshAdminSettings();
  }, [user, refreshAdminSettings]);
  const [tab, setTab] = useState(() => {
    // Deep-link support: `/admin?tab=customers` (used by the New User
    // Notification email, etc.) selects that tab immediately, before the
    // permission-guard effect below runs.
    try {
      const q = new URLSearchParams(window.location.search).get('tab');
      if (q) return q;
    } catch { /* ignore */ }
    return 'products';
  });
  // If the user's first visit lands on a tab they don't have permission for,
  // silently switch them to the first allowed tab. Runs once after auth resolves.
  useEffect(() => {
    if (!user?.is_admin) return;
    const allowed = TABS.filter(t => {
      if (t.super) return !!user.is_super_admin;
      if (!t.perm) return true;
      return !!user.is_super_admin || !!(user.admin_permissions || {})[t.perm];
    });
    if (!allowed.find(t => t.id === tab) && allowed.length) {
      setTab(allowed[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catBusy, setCatBusy] = useState(null);   // 'download' | 'upload' | null
  const [hero, setHero] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [overridesCustomer, setOverridesCustomer] = useState(null);

  const loadAll = async () => {
    setDataLoading(true);
    try {
      const [p, c, h] = await Promise.all([
        api.get('/products'), api.get('/categories'), api.get('/hero-slides'),
      ]);
      // The public /products endpoint materialises virtual per-variant cards
      // (they share the parent's `id` and carry `variant_id`). In admin we
      // must show only the master rows — variants are edited inline via the
      // VariantsEditor. Rendering the virtual cards here would let a variant's
      // "Delete product" button destroy the parent document.
      setProducts((p.data || []).filter(x => !x.variant_id));
      setCategories(c.data); setHero(h.data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setDataLoading(false); }
  };

  const loadOrders = async () => {
    try { const res = await api.get('/admin/orders'); setOrders(res.data); }
    catch (e) { toast.error('Failed to load orders'); }
  };
  const loadCustomers = async (filters = {}) => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '' && v != null));
      const res = await api.get('/admin/customers', { params });
      setCustomers(res.data);
    } catch (e) { toast.error('Failed to load customers'); }
  };

  useEffect(() => { if (user?.is_admin) loadAll(); }, [user]);
  useEffect(() => {
    if (!user?.is_admin) return;
    if (tab === 'orders') loadOrders();
    if (tab === 'customers') loadCustomers();
  }, [tab, user]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F46A25]" /></div>;
  if (!user) return <Navigate to="/login?redirect=/admin" replace />;
  if (!user.is_admin) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <ShieldCheck className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-[#12263A] mb-2">Admin access required</h1>
        <p className="text-neutral-600 mb-6">Your account doesn&apos;t have admin privileges. The first registered user becomes admin automatically.</p>
        <Link to="/" className="inline-block bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-6 py-3 rounded-sm">Back to home</Link>
      </div>
    );
  }

  // Surface the real reason behind admin write failures. "Update failed" alone
  // hid rate-limits, expired tokens, and validation errors — this helper shows
  // the backend detail, HTTP status, and network error message.
  const errMsg = (e, fallback = 'Update failed') => {
    const status = e.response?.status ? ` (HTTP ${e.response.status})` : '';
    const detail = e.response?.data?.detail || e.message || fallback;
    return `${fallback}${status}: ${detail}`;
  };

  // -------- Handlers --------
  const updateProduct = async (id, patch) => {
    try { const r = await api.put(`/admin/products/${id}`, patch); setProducts(prev => prev.map(p => p.id === id ? r.data : p)); }
    catch (e) { toast.error(errMsg(e)); throw e; }
  };
  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await api.delete(`/admin/products/${id}`); setProducts(prev => prev.filter(p => p.id !== id)); toast.success('Deleted'); }
    catch (e) { toast.error('Delete failed'); }
  };
  const duplicateProduct = async (id) => {
    const copyReviews = window.confirm('Also copy the reviews to the duplicate?\n\nClick OK to include reviews, Cancel to start with an empty review list.');
    try {
      const r = await api.post(`/admin/products/${id}/duplicate?copy_reviews=${copyReviews}`);
      // Refetch full list so the new one appears in the right category with correct order
      const all = await api.get('/products');
      setProducts((all.data || []).filter(x => !x.variant_id));
      toast.success(`Duplicated as "${r.data.name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate failed');
    }
  };
  const createProduct = async (slug) => {
    try {
      const r = await api.post('/admin/products', {
        name: 'New product', brand: 'Brand', price: 9.99, description: 'Description here',
        image: 'https://images.unsplash.com/photo-1607870411590-d5e9e06da09a?w=600',
        category: slug || categories[0]?.slug || 'tools', sku: 'SKU-' + Date.now(), stock: 10, rating: 4.5, reviews: 0
      });
      setProducts(prev => [r.data, ...prev]); toast.success('Product created');
    } catch (e) { toast.error('Create failed'); }
  };
  const updateCategory = async (id, patch) => {
    try { const r = await api.put(`/admin/categories/${id}`, patch); setCategories(prev => prev.map(c => c.id === id ? r.data : c)); }
    catch (e) { toast.error(errMsg(e)); throw e; }
  };

  const downloadCategoriesExcel = async () => {
    setCatBusy('download');
    try {
      const token = localStorage.getItem('bq_token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/categories/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ymd = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `dr-tools-categories-${ymd}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${categories.length} categories`);
    } catch (e) { toast.error('Download failed'); }
    finally { setCatBusy(null); }
  };

  const uploadCategoriesExcel = async (file) => {
    setCatBusy('upload');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/admin/categories/upload-excel', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { created = 0, updated = 0, error_count = 0, errors = [] } = res.data;
      if (error_count > 0) toast.error(`${created} created, ${updated} updated · ${error_count} row(s) rejected. First: ${errors[0]}`);
      else toast.success(`Imported: ${created} new, ${updated} updated`);
      const c = await api.get('/categories');
      setCategories(c.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setCatBusy(null); }
  };
  const createCategory = async (parent_id = null) => {
    const suffix = Date.now().toString().slice(-4);
    try {
      const r = await api.post('/admin/categories', {
        name: parent_id ? 'New sub-category' : 'New category',
        slug: `new-${suffix}`,
        image: 'https://images.unsplash.com/photo-1607870411590-d5e9e06da09a?w=600',
        parent_id: parent_id || null,
      });
      setCategories(prev => [...prev, r.data]);
      toast.success(parent_id ? 'Sub-category added' : 'Category added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };
  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category (and all its sub-categories)?')) return;
    try {
      const r = await api.delete(`/admin/categories/${id}`);
      // Reload categories to reflect cascading deletion
      const res = await api.get('/categories');
      setCategories(res.data);
      toast.success(`Deleted ${r.data.deleted} categor${r.data.deleted === 1 ? 'y' : 'ies'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };
  const updateHero = async (id, patch) => {
    try { const r = await api.put(`/admin/hero-slides/${id}`, patch); setHero(prev => prev.map(h => h.id === id ? r.data : h)); }
    catch (e) { toast.error(errMsg(e)); throw e; }
  };
  const addHero = async () => {
    try {
      const r = await api.post('/admin/hero-slides', {});
      setHero(prev => [...prev, r.data]);
      toast.success('Banner added — edit it below');
    } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteHero = async (id) => {
    if (!window.confirm('Delete this hero banner? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/hero-slides/${id}`);
      setHero(prev => prev.filter(h => h.id !== id));
      toast.success('Banner deleted');
    } catch (e) { toast.error(errMsg(e)); }
  };
  const updateOrderStatus = async (oid, status) => {
    try { const r = await api.put(`/admin/orders/${oid}`, { status }); setOrders(prev => prev.map(o => o.id === oid ? r.data : o)); toast.success('Status updated'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const deleteOrder = async (oid) => {
    try {
      await api.delete(`/admin/orders/${oid}`);
      setOrders(prev => prev.filter(o => o.id !== oid));
      toast.success('Order deleted');
    } catch (e) { toast.error(errMsg(e)); }
  };
  const loadOrdersFiltered = async (filters) => {
    const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '' && v != null));
    try { const r = await api.get('/admin/orders', { params }); setOrders(r.data); }
    catch (e) { toast.error('Failed'); }
  };
  const exportOrders = async (filters) => {
    const params = Object.fromEntries(Object.entries(filters || {}).filter(([_, v]) => v !== '' && v != null));
    try {
      const res = await api.get('/admin/orders/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `orders-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch (e) { toast.error('Export failed'); }
  };
  const exportCustomers = async () => {
    try {
      const res = await api.get('/admin/customers/export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `customers-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch (e) { toast.error('Export failed'); }
  };
  const resetPassword = async (uid, email) => {
    const pwd = window.prompt(`Enter a new password for ${email} (min 8 chars, letter + digit):`);
    if (!pwd) return;
    if (pwd.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    try {
      await api.put(`/admin/customers/${uid}/password`, { new_password: pwd });
      toast.success('Password reset. Share it with the user.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const toggleAdmin = async (uid, current) => {
    try { const r = await api.put(`/admin/customers/${uid}`, { is_admin: !current }); setCustomers(prev => prev.map(c => c.id === uid ? r.data : c)); toast.success('Updated'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const updateCustomerName = async (uid, name) => {
    try { const r = await api.put(`/admin/customers/${uid}`, { name }); setCustomers(prev => prev.map(c => c.id === uid ? r.data : c)); }
    catch (e) { toast.error(errMsg(e)); throw e; }
  };
  const updateCustomerType = async (uid, user_type) => {
    try { const r = await api.put(`/admin/customers/${uid}`, { user_type }); setCustomers(prev => prev.map(c => c.id === uid ? r.data : c)); toast.success('Type updated'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const deleteCustomer = async (uid) => {
    if (!window.confirm('Delete this customer? This cannot be undone.')) return;
    try { await api.delete(`/admin/customers/${uid}`); setCustomers(prev => prev.filter(c => c.id !== uid)); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };
  const approveCustomer = async (uid) => {
    try { const r = await api.put(`/admin/customers/${uid}/approve`); setCustomers(prev => prev.map(c => c.id === uid ? r.data : c)); toast.success('Trade tier approved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };
  const rejectCustomer = async (uid) => {
    if (!window.confirm('Reject this trade upgrade? The user stays as an end user.')) return;
    try { const r = await api.put(`/admin/customers/${uid}/reject`); setCustomers(prev => prev.map(c => c.id === uid ? r.data : c)); toast.success('Rejected'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };
  const updateSettings = async (patch) => {
    try { await api.put('/admin/settings', patch); await refreshSettings(); await refreshAdminSettings(); toast.success('Saved'); }
    catch (e) { toast.error('Save failed'); throw e; }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6">
      <div className="mb-6">
        <Link to="/" className="text-xs text-neutral-600 hover:text-[#F46A25] flex items-center gap-1 mb-2"><ArrowLeft className="w-3 h-3" /> Back to store</Link>
        <h1 className="text-3xl md:text-4xl font-black text-[#12263A] flex items-center gap-2"><ShieldCheck className="w-8 h-8 text-[#F46A25]" /> Admin</h1>
        <p className="text-sm text-neutral-600 mt-1">Click any text to edit • Hover an image and click to upload a new one</p>
      </div>

      <div className="flex border-b border-neutral-200 mb-6 overflow-x-auto">
        {TABS.filter(t => {
          if (t.super) return !!user.is_super_admin;
          if (!t.perm) return true;
          return !!user.is_super_admin || !!(user.admin_permissions || {})[t.perm];
        }).map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`} className={`px-5 py-3 text-sm font-bold flex items-center gap-2 transition-colors whitespace-nowrap ${tab === t.id ? 'text-[#F46A25] border-b-2 border-[#F46A25]' : 'text-neutral-600 hover:text-[#12263A]'}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {dataLoading && ['products','categories','hero'].includes(tab) ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F46A25]" /></div>
      ) : (
        <>
          {tab === 'products' && (
            <CategoryProductsAccordion
              categories={categories}
              products={products}
              updateProduct={updateProduct}
              deleteProduct={deleteProduct}
              duplicateProduct={duplicateProduct}
              onCreateProduct={createProduct}
              onProductsReloaded={async () => {
                const p = await api.get('/products');
                setProducts((p.data || []).filter(x => !x.variant_id));
              }}
            />
          )}

          {tab === 'categories' && (
            <div>
              <div className="flex justify-between items-center flex-wrap gap-3 mb-4">
                <span className="text-sm text-neutral-600">{categories.length} categories total</span>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={downloadCategoriesExcel}
                    disabled={catBusy}
                    data-testid="admin-download-categories-excel"
                    className="border border-[#F46A25] bg-orange-50 hover:bg-orange-100 text-[#F46A25] font-bold text-xs px-3 py-2 rounded-sm flex items-center gap-2 disabled:opacity-60"
                    title="Download every category (with SEO intro) as one Excel file"
                  >
                    {catBusy === 'download' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                    Download Excel
                  </button>
                  <label
                    data-testid="admin-upload-categories-excel"
                    className={`border border-[#12263A] bg-[#12263A] hover:bg-[#F46A25] text-white font-bold text-xs px-3 py-2 rounded-sm flex items-center gap-2 cursor-pointer ${catBusy ? 'opacity-60 pointer-events-none' : ''}`}
                    title="Upload a filled-in categories .xlsx to bulk create/update"
                  >
                    {catBusy === 'upload' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    Upload Excel
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCategoriesExcel(f); e.target.value = ''; }}
                    />
                  </label>
                  <button onClick={() => createCategory(null)} data-testid="admin-add-category" className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm px-4 py-2 rounded-sm flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add category
                  </button>
                </div>
              </div>
              <CategoryTree
                categories={categories}
                onUpdate={updateCategory}
                onDelete={deleteCategory}
                onAddSub={(parentId) => createCategory(parentId)}
                onReload={async () => {
                  const c = await api.get('/categories');
                  setCategories(c.data);
                }}
              />
            </div>
          )}

          {tab === 'hero' && (
            <HeroBannersAdmin hero={hero} onUpdate={updateHero} onAdd={addHero} onDelete={deleteHero} />
          )}

          {tab === 'orders' && (
            <OrdersAdmin orders={orders} onStatusChange={updateOrderStatus} onFilter={loadOrdersFiltered} onExport={exportOrders} onDelete={deleteOrder} />
          )}

          {tab === 'customers' && (
            <CustomersAdmin
              customers={customers}
              currentUserId={user.id}
              onToggleAdmin={toggleAdmin}
              onDelete={deleteCustomer}
              onNameChange={updateCustomerName}
              onTypeChange={updateCustomerType}
              onResetPassword={resetPassword}
              onExport={exportCustomers}
              onOpenOverrides={setOverridesCustomer}
              onApprove={approveCustomer}
              onReject={rejectCustomer}
              onFilter={loadCustomers}
            />
          )}

          {tab === 'reviews' && <ReviewsAdmin />}

          {tab === 'faqs' && <FAQsAdmin />}

          {tab === 'emails' && <EmailTemplatesAdmin />}

          {tab === 'promos' && <PromoCodesAdmin />}

          {tab === 'abandoned' && <AbandonedCartsAdmin />}

          {tab === 'settings' && (
            <SettingsAdmin settings={adminSettings || siteSettings} onSave={updateSettings} iconOptions={ICON_OPTIONS} />
          )}

          {tab === 'homepage' && (
            <HomepageCurationAdmin settings={adminSettings || siteSettings} onSave={updateSettings} />
          )}

          {tab === 'team' && <ManageAdminsAdmin />}
          {tab === 'activity' && <ActivityLogAdmin />}
          {tab === 'search-analytics' && <SearchAnalyticsAdmin />}
        </>
      )}
      <CustomerOverridesDialog
        customer={overridesCustomer}
        open={!!overridesCustomer}
        onClose={() => setOverridesCustomer(null)}
        onSaved={() => loadCustomers()}
      />
    </div>
  );
};

// ---------------- Category tree moved to /components/admin/CategoryTree.jsx ----------------

export default AdminPage;
